/**
 * Timezone Utility Functions
 * Converts UTC times from the backend into the user's profile timezone.
 *
 * Jun 2026 fix: previously used a hardcoded STANDARD-time offset table
 * (e.g. Mountain = -7 all year), which showed times 1 hour behind during
 * daylight saving (a Calgary user saw Pacific-looking times in summer).
 * All conversion now goes through Intl.DateTimeFormat with the IANA
 * timeZone option — DST-aware for every zone, no manual offsets.
 */

/**
 * Display locale (Aug 2026 Europe launch): follows the in-app language
 * switcher; for English we pass `undefined` so the BROWSER locale decides —
 * European browsers automatically get dd/mm/yyyy dates and 24-hour times.
 */
const LOCALE_BY_LANG = { es: 'es-ES', fr: 'fr-FR', de: 'de-DE', it: 'it-IT' };
export const getDisplayLocale = () => {
  try {
    const lang = (localStorage.getItem('hinestime_language') || '').split('-')[0];
    return LOCALE_BY_LANG[lang] || undefined;
  } catch (e) {
    return undefined;
  }
};

// Fallback abbreviations if Intl can't produce one (rare)
const TIMEZONE_ABBREVIATIONS = {
  'America/St_Johns': { standard: 'NST', daylight: 'NDT' },
  'America/Halifax': { standard: 'AST', daylight: 'ADT' },
  'America/Toronto': { standard: 'EST', daylight: 'EDT' },
  'America/New_York': { standard: 'EST', daylight: 'EDT' },
  'America/Chicago': { standard: 'CST', daylight: 'CDT' },
  'America/Winnipeg': { standard: 'CST', daylight: 'CDT' },
  'America/Denver': { standard: 'MST', daylight: 'MDT' },
  'America/Edmonton': { standard: 'MST', daylight: 'MDT' },
  'America/Phoenix': { standard: 'MST', daylight: 'MST' },
  'America/Los_Angeles': { standard: 'PST', daylight: 'PDT' },
  'America/Vancouver': { standard: 'PST', daylight: 'PDT' },
  'America/Anchorage': { standard: 'AKST', daylight: 'AKDT' },
  'Pacific/Honolulu': { standard: 'HST', daylight: 'HST' },
};

/**
 * Parse a backend datetime as UTC.
 * Backend datetimes are always UTC, but some are serialized WITHOUT a
 * trailing 'Z' (naive ISO strings from MongoDB). Appending 'Z' prevents
 * the browser from misreading them as local time.
 */
const parseUTC = (value) => {
  if (value instanceof Date) return value;
  if (typeof value === 'string' && value.includes('T') && !/([zZ]|[+-]\d{2}:?\d{2})$/.test(value)) {
    return new Date(value + 'Z');
  }
  return new Date(value);
};

/**
 * Get the timezone offset (in minutes) of an IANA timezone at a given date.
 * DST-aware — used only for the DST check below.
 */
const getZoneOffsetMinutes = (date, timezone) => {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  });
  const parts = {};
  dtf.formatToParts(date).forEach(p => { parts[p.type] = p.value; });
  const asUTC = Date.UTC(
    Number(parts.year), Number(parts.month) - 1, Number(parts.day),
    Number(parts.hour) % 24, Number(parts.minute), Number(parts.second)
  );
  return (asUTC - date.getTime()) / 60000;
};

/**
 * Check if a date is in daylight saving time for a given timezone (DST-aware).
 */
export const isDaylightSavingTime = (date, timezone) => {
  try {
    const jan = getZoneOffsetMinutes(new Date(Date.UTC(date.getFullYear(), 0, 1)), timezone);
    const jul = getZoneOffsetMinutes(new Date(Date.UTC(date.getFullYear(), 6, 1)), timezone);
    return getZoneOffsetMinutes(date, timezone) === Math.max(jan, jul) && jan !== jul;
  } catch (error) {
    return false;
  }
};

/* ──────────────────────────────────────────────────────────────────────────
 * Daylight-saving control (Oct 2026).
 * A user's / company's `timezone` may be a SPEC string:
 *   "America/Edmonton"                       → follow IANA rules (auto)
 *   "America/Edmonton|standard|2026-03-08"   → no DST: stay on the winter clock (MST) from 8 Mar 2026
 *   "America/Vancouver|daylight"             → no DST: stay on the summer clock (PDT) always
 * Every formatter below resolves the spec per-timestamp via resolveTimeZone().
 * ────────────────────────────────────────────────────────────────────────── */
export const DST_MODES = ['auto', 'standard', 'daylight'];

export const parseTzSpec = (spec) => {
  if (!spec || typeof spec !== 'string') return { region: 'America/Toronto', mode: 'auto', from: null };
  const [r, m, f] = spec.split('|');
  const region = (r || '').trim() || 'America/Toronto';
  const mode = DST_MODES.includes((m || '').trim()) ? m.trim() : 'auto';
  const from = /^\d{4}-\d{2}-\d{2}$/.test((f || '').trim()) ? f.trim() : null;
  return { region, mode, from };
};
export const tzRegion = (spec) => parseTzSpec(spec).region;
export const buildTzSpec = (region, mode = 'auto', from = null) => {
  if (!mode || mode === 'auto' || mode === 'inherit') return region;
  return `${region}|${mode}${from ? `|${from}` : ''}`;
};

const _fixedCache = {};
/** Standard/daylight offsets (minutes) and abbreviations of a region, from this year's Jan/Jul. */
const regionDstInfo = (region) => {
  if (_fixedCache[region]) return _fixedCache[region];
  const y = new Date().getFullYear();
  const jan = new Date(Date.UTC(y, 0, 15, 12)), jul = new Date(Date.UTC(y, 6, 15, 12));
  let info;
  try {
    const oJan = getZoneOffsetMinutes(jan, region), oJul = getZoneOffsetMinutes(jul, region);
    const stdDate = oJan <= oJul ? jan : jul, dlDate = oJan <= oJul ? jul : jan;
    info = {
      hasDst: oJan !== oJul,
      standard: { offset: Math.min(oJan, oJul), abbr: rawAbbreviation(region, stdDate) },
      daylight: { offset: Math.max(oJan, oJul), abbr: rawAbbreviation(region, dlDate) },
    };
  } catch (e) {
    info = { hasDst: false, standard: { offset: 0, abbr: '' }, daylight: { offset: 0, abbr: '' } };
  }
  _fixedCache[region] = info;
  return info;
};
/** IANA fixed zone for a whole-hour offset: -420 → 'Etc/GMT+7' (sign is inverted by convention). */
const etcZoneFor = (offsetMinutes) => {
  if (offsetMinutes % 60 !== 0) return null;
  const h = offsetMinutes / 60;
  if (h === 0) return 'Etc/UTC';
  return `Etc/GMT${h > 0 ? '-' : '+'}${Math.abs(h)}`;
};
/** Does the fixed (no-DST) clock apply to this instant under the spec? */
const fixedApplies = (parsed, date) => {
  if (parsed.mode === 'auto') return false;
  if (!parsed.from) return true;
  const d = date instanceof Date ? date : new Date(date || Date.now());
  // switch at local midnight on `from` (approximate with the fixed offset)
  const info = regionDstInfo(parsed.region);
  const off = (parsed.mode === 'standard' ? info.standard : info.daylight).offset;
  const [yy, mm, dd] = parsed.from.split('-').map(Number);
  return d.getTime() >= Date.UTC(yy, mm - 1, dd) - off * 60000;
};
/**
 * Resolve a timezone spec to an IANA name Intl understands, for a given instant.
 * Plain IANA names pass straight through.
 */
export const resolveTimeZone = (spec, date = new Date()) => {
  if (!spec) return spec;
  if (!spec.includes('|')) return spec;
  const parsed = parseTzSpec(spec);
  if (!fixedApplies(parsed, date)) return parsed.region;
  const info = regionDstInfo(parsed.region);
  if (!info.hasDst) return parsed.region;
  const off = (parsed.mode === 'standard' ? info.standard : info.daylight).offset;
  return etcZoneFor(off) || parsed.region;
};
const rawAbbreviation = (timezone, date) => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'short' }).formatToParts(date);
    const tz = parts.find(p => p.type === 'timeZoneName');
    if (tz && tz.value) return tz.value;
  } catch (error) { /* fall through */ }
  const abbr = TIMEZONE_ABBREVIATIONS[timezone];
  if (abbr) return isDaylightSavingTime(date, timezone) ? abbr.daylight : abbr.standard;
  const segs = (timezone || '').split('/');
  return segs[segs.length - 1].replace(/_/g, ' ');
};

/**
 * Get timezone abbreviation for a given timezone (or DST spec) at a given date (e.g. 'MDT').
 */
export const getTimezoneAbbreviation = (timezone, date = new Date()) => {
  if (timezone && timezone.includes('|')) {
    const parsed = parseTzSpec(timezone);
    if (fixedApplies(parsed, date)) {
      const info = regionDstInfo(parsed.region);
      if (info.hasDst) return (parsed.mode === 'standard' ? info.standard : info.daylight).abbr;
    }
    return rawAbbreviation(parsed.region, date);
  }
  return rawAbbreviation(timezone, date);
};

const MODE_WORDS = { standard: 'standard (winter) clock all year', daylight: 'summer (daylight) clock all year' };
/** Human description of a spec for settings screens: "Canada Mountain · no DST, MST all year from Mar 8, 2026". */
export const describeTzSpec = (spec, regionLabel = null) => {
  const parsed = parseTzSpec(spec);
  const label = regionLabel || parsed.region.replace(/^(America|Europe|Pacific|Asia|Australia|Africa)\//, '').replace(/_/g, ' ');
  if (parsed.mode === 'auto') return label;
  const info = regionDstInfo(parsed.region);
  const abbr = (parsed.mode === 'standard' ? info.standard : info.daylight).abbr;
  let out = `${label} · no DST (${abbr} all year)`;
  if (parsed.from) {
    const [y, m, d] = parsed.from.split('-').map(Number);
    out += ` from ${new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
  }
  return out;
};

/**
 * Convert UTC datetime string to a Date object (UTC-normalised parse).
 */
export const convertUTCToUserTimezone = (utcDateString, userTimezone = 'America/Toronto') => {
  if (!utcDateString) return null;
  try {
    const utcDate = parseUTC(utcDateString);
    return isNaN(utcDate.getTime()) ? null : utcDate;
  } catch (error) {
    console.error('Error converting timezone:', error);
    return null;
  }
};

/**
 * Get effective timezone (use browser's if user hasn't set one)
 */
const getEffectiveTimezone = (userTimezone, date = new Date()) => {
  if (userTimezone) return resolveTimeZone(userTimezone, date);
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    return 'America/Toronto';
  }
};

/**
 * Format time with timezone abbreviation (e.g. '2:30 PM MDT').
 * DST-aware via Intl.
 */
export const formatTimeWithTimezone = (utcDateString, userTimezone, options = {}) => {
  if (!utcDateString) return 'N/A';
  const { showDate = false, showSeconds = false, showTimezone = true } = options;

  try {
    const utcDate = parseUTC(utcDateString);
    if (isNaN(utcDate.getTime())) return 'N/A';
    const effectiveTimezone = getEffectiveTimezone(userTimezone, utcDate);

    let formatted = '';
    if (showDate) {
      formatted += new Intl.DateTimeFormat(getDisplayLocale(), {
        timeZone: effectiveTimezone, year: 'numeric', month: 'short', day: 'numeric',
      }).format(utcDate) + ' at ';
    }

    formatted += new Intl.DateTimeFormat(getDisplayLocale(), {
      timeZone: effectiveTimezone,
      hour: 'numeric', minute: '2-digit',
      ...(showSeconds ? { second: '2-digit' } : {}),
    }).format(utcDate);

    if (showTimezone) {
      formatted += ` ${getTimezoneAbbreviation(userTimezone || effectiveTimezone, utcDate)}`;
    }
    return formatted;
  } catch (error) {
    console.error('Error formatting time:', error, { utcDateString, userTimezone });
    return 'N/A';
  }
};

/**
 * Format date and time with timezone (e.g. 'Jan 15, 2025 at 2:30 PM MST').
 * DST-aware via Intl.
 */
export const formatDateTimeWithTimezone = (utcDateString, userTimezone) => {
  if (!utcDateString) return 'N/A';

  try {
    const utcDate = parseUTC(utcDateString);
    if (isNaN(utcDate.getTime())) return 'N/A';
    const effectiveTimezone = getEffectiveTimezone(userTimezone, utcDate);

    const dateStr = new Intl.DateTimeFormat(getDisplayLocale(), {
      timeZone: effectiveTimezone, year: 'numeric', month: 'short', day: 'numeric',
    }).format(utcDate);
    const timeStr = new Intl.DateTimeFormat(getDisplayLocale(), {
      timeZone: effectiveTimezone, hour: 'numeric', minute: '2-digit',
    }).format(utcDate);

    return `${dateStr} at ${timeStr} ${getTimezoneAbbreviation(userTimezone || effectiveTimezone, utcDate)}`;
  } catch (error) {
    console.error('Error formatting datetime:', error);
    return 'N/A';
  }
};

/**
 * Format date only (no time), in the user's timezone.
 */
export const formatDateOnly = (utcDateString, userTimezone) => {
  if (!utcDateString) return 'N/A';
  try {
    const date = parseUTC(utcDateString);
    if (isNaN(date.getTime())) return 'N/A';
    return new Intl.DateTimeFormat(getDisplayLocale(), {
      year: 'numeric', month: 'short', day: 'numeric',
      timeZone: getEffectiveTimezone(userTimezone, date),
    }).format(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'N/A';
  }
};

/**
 * Calculate duration between two UTC datetime strings, in hours.
 */
export const calculateDuration = (startUTC, endUTC) => {
  if (!startUTC || !endUTC) return 0;
  try {
    const diffHours = (parseUTC(endUTC) - parseUTC(startUTC)) / (1000 * 60 * 60);
    return Math.round(diffHours * 100) / 100;
  } catch (error) {
    console.error('Error calculating duration:', error);
    return 0;
  }
};

/**
 * Format duration in hours to readable string.
 */
export const formatDuration = (hours, detailed = false) => {
  if (!hours) return '0 hrs';
  if (detailed) {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  return `${hours.toFixed(2)} hrs`;
};

/**
 * Get user's current time in their timezone.
 */
export const getCurrentTimeInUserTimezone = (userTimezone) => {
  const now = new Date();
  const effectiveTimezone = getEffectiveTimezone(userTimezone, now);
  const formatter = new Intl.DateTimeFormat(getDisplayLocale(), {
    hour: 'numeric', minute: '2-digit', second: '2-digit',
    timeZone: effectiveTimezone,
  });
  return `${formatter.format(now)} ${getTimezoneAbbreviation(userTimezone || effectiveTimezone, now)}`;
};
